/* eslint-disable custom-rules/modal-and-drawer-requires-title */
import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Drawer } from '@uhg-abyss/web/ui/Drawer';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Popover } from '@uhg-abyss/web/ui/Popover';
import { Text } from '@uhg-abyss/web/ui/Text';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { ConstantsLagoon } from '../../../common/ConstantsLagoon';
import { useLagoon } from '../../../hooks/useLagoon';
import { useNotAvailableUnknownText } from '../../../hooks/useNotAvailableUnknownText';
import { FacilityLocationsResponse } from '../../../models/FacilityDetails';
import { ProviderLocation } from '../../../models/ProviderDetails';
import { useTypeaheadStore } from '../../../store/useTypeaheadStore';
import { TypeaheadState } from '../../../store/useTypeaheadStore/typeaheadStore';
import { getUiMessageFromLagoon } from '../../../utils/common';
import { phoneOnly } from '../../ConstantsStyles';
import { popoverMobileStyle } from '../../DataCard/Miles.style';
import { handleLinkAndModalTrack } from '../../Utils/adobeTrackUtils';
import { MilesContent } from './ProviderLocationsDetails.style';

type Props = {
  selectedLocation?: ProviderLocation | FacilityLocationsResponse;
  title: string;
  content?: string;
  widths?: number;
  position?: string;
  align?: string;
  alignOffset?: number;
  locationForAnalytics?: string;
  ariaLabel: string;
  bestMatchTitle?: string;
  smartChoiceTitle?: string;
  relevanceLabel?: string;
};

export const PopOverWrapper = styled('div', {
  '.abyss-popover-trigger ~ div': {
    zIndex: '999 !important',
  },
});

export const Distance = ({
  selectedLocation,
  title,
  content = '',
  widths = 220,
  position = 'bottom',
  locationForAnalytics,
  ariaLabel,
  bestMatchTitle,
  smartChoiceTitle,
  relevanceLabel,
}: Props) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const mobileScreen = useMediaQuery(phoneOnly);
  const typeaheadState = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      sectionType: state.typeaheadSearchStore.sectionType,
    }))
  );
  const { sectionType = '' } = typeaheadState;
  const lagoonData = useLagoon('ui-messaging')();
  const smartChoiceContent = getUiMessageFromLagoon(
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_INFO_CONTENT,
    lagoonData
  );
  const preferredProviderContent = getUiMessageFromLagoon(
    ConstantsLagoon.UI_MESSAGING.INFO_MODAL_CONTENT_PREFERRED_PROVIDERS,
    lagoonData
  );
  const preferredFacilityMean = getUiMessageFromLagoon(
    ConstantsLagoon.UI_MESSAGING.WHAT_IS_PREFERRED_FACILITY,
    lagoonData
  );
  const isFacility = sectionType?.toLowerCase() === 'facility';
  useEffect(() => {
    if (isOpen) {
      const linkName: string = 'distance-popover';
      const linkLocation: string = `body:${
        locationForAnalytics ?? 'location detail distance'
      }`;
      const modalName: string = 'distance';
      handleLinkAndModalTrack(linkName, linkLocation, modalName);
    }
  }, [isOpen]);

  const displayContent = () => (
    <Flex
      alignItems="center"
      data-auto-testid="provider-location-detail-miles-Info"
      data-testid="provider-location-detail-miles-Info"
      onClick={() => setIsOpen(true)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setIsOpen(true);
        }
      }}
    >
      <IconSymbol icon="info" size={16} variant="outlined" />
      <Text
        css={{
          lineHeight: '18px',
          fontSize: '14px',
          color: '$core.color.brand.60',
          paddingLeft: '8px',
          fontWeight: '400',
        }}
      >
        {smartChoiceTitle
          ? t('SMART_CHOICE_INFO')
          : bestMatchTitle === relevanceLabel
          ? t('RELEVANCE_MEAN')
          : isFacility
          ? preferredFacilityMean
          : t('PREFFERED_PROVIDER_MEAN')}
      </Text>
    </Flex>
  );

  const { providerLocations: notAvailableOrUnknownText } =
    useNotAvailableUnknownText();

  return (
    <React.Fragment>
      <Flex
        alignItems="center"
        data-auto-testid="preferred-provider-info-icon"
        data-testid="preferred-provider-info-icon"
      >
        {selectedLocation ? (
          <React.Fragment>
            <IconSymbol icon="directions" />
            {selectedLocation?.distance?.length ? (
              <MilesContent>
                {`${parseFloat(selectedLocation.distance).toFixed(1)} ${t(
                  'miles'
                )}`}
              </MilesContent>
            ) : (
              <MilesContent>{notAvailableOrUnknownText}</MilesContent>
            )}
          </React.Fragment>
        ) : null}

        {!mobileScreen ? (
          <PopOverWrapper>
            <Popover
              aria-label={ariaLabel}
              content={
                smartChoiceTitle
                  ? smartChoiceContent
                  : isFacility
                  ? preferredProviderContent
                  : content
              }
              css={{
                'abyss-popover-trigger': {
                  display: 'flex',
                  alignItem: 'center',
                },
              }}
              data-auto-testid="provider-location-detail-miles-Info-popover"
              data-testid="provider-location-detail-miles-Info-popover"
              onOpenChange={() => {
                setIsOpen(!isOpen);
              }}
              position={position}
              tabIndex={0}
              title={
                <Text fontSize="sm">
                  {smartChoiceTitle
                    ? t('SORT_OPTIONS.RECOMMENDED_PROVIDERS')
                    : title}
                </Text>
              }
              width={widths}
            >
              {displayContent()}
            </Popover>
          </PopOverWrapper>
        ) : (
          <React.Fragment>
            {displayContent()}
            <Drawer
              css={popoverMobileStyle}
              data-auto-testid="provider-location-detail-miles-Info-popover"
              data-testid="provider-location-detail-miles-Info-popover"
              header={title}
              isOpen={isOpen}
              onClose={() => setIsOpen(false)}
              position="right"
            >
              <div>
                {smartChoiceTitle
                  ? smartChoiceContent
                  : isFacility
                  ? preferredProviderContent
                  : content}
              </div>
            </Drawer>
          </React.Fragment>
        )}
      </Flex>
    </React.Fragment>
  );
};
